import { Zap, Sparkles, Crown } from 'lucide-react';

/**
 * Quality tier options mapped to backend model IDs.
 * Empty value = no override = backend picks the pool primary (cheapest/fastest).
 */
export const QUALITY_TIERS = {
  free: [], // no picker — backend handles everything
  pro: [
    { label: 'Standard', value: '', description: 'Fast and reliable', icon: Zap },
    { label: 'Quality', value: 'z-ai/glm-5.1', description: 'Best for code quality', icon: Sparkles },
  ],
  business: [
    { label: 'Fast', value: '', description: 'Quickest generation', icon: Zap },
    { label: 'Quality', value: 'z-ai/glm-5.1', description: 'Best for code quality', icon: Sparkles },
    { label: 'Premium', value: 'gemini-3.1-pro-preview', description: 'Highest fidelity', icon: Crown },
  ],
  enterprise: null, // uses business tiers
};

/**
 * QualityPicker — pill toggle for AI generation quality level.
 * Replaces raw model ID dropdowns with human-readable labels.
 *
 * @param {string} tier - User's subscription tier (free/pro/business/enterprise)
 * @param {string} value - Currently selected model value ('' = default)
 * @param {function} onChange - Called with the model value string
 */
export default function QualityPicker({ tier = 'free', value = '', onChange }) {
  const options = QUALITY_TIERS[tier] || QUALITY_TIERS[tier === 'enterprise' ? 'business' : 'free'] || [];

  if (!options.length) return null; // free tier — no picker

  return (
    <div>
      <label
        style={{
          fontSize: '11px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--orbis-t3)',
          marginBottom: '8px',
          display: 'block',
        }}
      >
        Generation Quality
      </label>
      <div className="flex gap-2 flex-wrap">
        {options.map((opt) => {
          const isActive = value === opt.value;
          const Icon = opt.icon;
          return (
            <button
              key={opt.label}
              type="button"
              onClick={() => onChange(opt.value)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 16px',
                borderRadius: '100px',
                fontSize: '12px',
                fontWeight: isActive ? 500 : 400,
                fontFamily: 'var(--orbis-font)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: isActive ? 'rgba(184,204,223,0.12)' : 'rgba(255,255,255,0.03)',
                border: isActive
                  ? '1px solid var(--orbis-blue)'
                  : '1px solid rgba(255,255,255,0.08)',
                color: isActive ? 'var(--orbis-blue)' : 'var(--orbis-t2)',
              }}
            >
              <Icon style={{ width: '13px', height: '13px', opacity: isActive ? 1 : 0.5 }} />
              {opt.label}
            </button>
          );
        })}
      </div>
      {options.find(o => o.value === value)?.description && (
        <p style={{ fontSize: '11px', color: 'var(--orbis-t3)', marginTop: '6px' }}>
          {options.find(o => o.value === value)?.description}
        </p>
      )}
    </div>
  );
}
